<!--
A select field on which all vee validation rules can be applied throught the "rules" prop.
See vee-validationrules.ts for list of available rules

Example use : 
 <SelectWithValidation
    rules="required"
    :tooltip="$t('input-email-tooltip')"
    :label="$t('input-email')"
    placeholder="e.g. EXAMPLE VALUE"
    v-model="something"
    :data="listofthings"
    :emptyMessage="Message when nothing is found"
  />
-->
<template>
  <ValidationProvider
    v-slot="{ errors, valid, handleChange, handleBlur }"
    :vid="(group != '' ? group + '_' : '') + vid"
    :name="($attrs.name as string) || ($attrs.label as string)"
    :rules="rulesWithoutRequiredRulesIfValidationDisabled"
    :model-value="innerValue"
    @update:model-value="updateValue"
  >
    <b-field
      :type="{
        'is-danger': showValidationErrors && errors[0],
        'is-success': valid,
      }"
      :message="showValidationErrors ? [errors[0]] : ''"
      :class="inline ? 'SelectWithValidation-field-inline' : 'SelectWithValidation-field'"
    >
      <template #label>
        <FormInputLabel
          :validation-activated="validationActivated"
          :show-validation-errors="showValidationErrors"
          :label="($attrs.label as string) || ''"
          :is-mandatory="isMandatory"
          :disabled="disabled"
          :input-notice="inputNotice"
          :tooltip="tooltip"
        />
      </template>

      <b-select
        v-model="innerValue"
        :placeholder="placeholder"
        :disabled="disabled"
        @update:model-value="
          (val: any) => {
            handleChange && handleChange(val)
          }
        "
        @blur="(event: any) => handleBlur && handleBlur(event)"
      >
        <template v-for="(option, i) in data" :key="i">
          <option :value="option">
            {{ selectLabels && selectLabels.length !== 0 ? selectLabels[i] : option }}
          </option>
        </template>
      </b-select>
    </b-field>
  </ValidationProvider>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import ValidationProvider from "@/services/validation/ValidationProvider.vue"
import FormInputLabel from "./FormInputLabel.vue"
import { BSelect, BField } from "buefy"

export default defineComponent({
  name: "SelectWithValidation",
  components: {
    ValidationProvider,
    FormInputLabel,
    BField,
    BSelect,
  },
  props: {
    group: {
      type: String,
      default: "",
    },
    vid: {
      type: String,
      required: false,
      default: "",
    },
    rules: {
      type: [String, Array],
      required: false,
      default: "",
    },
    modelValue: {
      type: [String, Object],
      required: false,
      default: null,
    },
    tooltip: {
      type: String,
      required: false,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    data: {
      type: Array,
      required: false,
      default: () => [],
    },
    selectLabels: {
      type: Array,
      required: false,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    inputNotice: {
      type: String,
      required: false,
      default: "",
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue", "manual-change"],
  data() {
    return {
      innerValue: null as any,
      isMandatory: false,
      rulesWithoutRequiredRulesIfValidationDisabled: "",
    }
  },
  watch: {
    innerValue(newVal: any, _oldValue: any): void {
      this.$emit("update:modelValue", newVal)
    },
    modelValue(newVal: any, _oldValue: any): void {
      this.innerValue = newVal
      if (newVal != _oldValue) {
        this.$emit("manual-change", newVal)
      }
    },
    rules(): void {
      this.onRulesChanged()
    },
    validationActivated(): void {
      this.onRulesChanged()
    },
  },
  created(): void {
    if (this.modelValue !== undefined && this.modelValue !== null) {
      this.innerValue = this.modelValue
    }
    // Handle both string and array formats for rules in created
    if (this.rules) {
      if (Array.isArray(this.rules)) {
        this.isMandatory = this.rules.includes("required")
      } else {
        this.isMandatory = this.rules.includes("required")
      }
    } else {
      this.isMandatory = false
    }
    this.onRulesChanged()
  },
  methods: {
    updateValue(newValue: any): void {
      this.innerValue = newValue
      // Émettre immédiatement pour synchroniser avec le parent
      this.$emit("update:modelValue", newValue)
    },
    onRulesChanged(): void {
      // Handle both string and array formats for rules
      if (this.rules) {
        if (Array.isArray(this.rules)) {
          this.isMandatory = this.rules.includes("required")
        } else {
          // String format like "required|email"
          this.isMandatory = this.rules.includes("required")
        }
      } else {
        this.isMandatory = false
      }

      if (this.rules) {
        this.rulesWithoutRequiredRulesIfValidationDisabled = this.rules.toString()
        if (!this.validationActivated) {
          this.rulesWithoutRequiredRulesIfValidationDisabled = this.rules
            .toString()
            .replace("required|", "")
            .replace("|required", "")
            .replace("required", "")
        }
      } else {
        this.rulesWithoutRequiredRulesIfValidationDisabled = ""
      }
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.SelectWithValidation-field,
.SelectWithValidation-field-inline {
  margin-bottom: $input-field-margin-bot;
}

.SelectWithValidation-field-inline {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

#app .SelectWithValidation-disabled {
  color: $text-default-color;
}

.fieldLabel {
  display: flex;
  justify-content: space-between;
  width: 100%;

  .mandatory {
    padding-left: 20px;
    color: $dark;
  }
}
</style>
